import React, { useEffect, useMemo, useState } from 'react';
import {
  CASE_EVENT_TYPES,
  HEARING_MODES,
  ORDER_OUTCOMES,
  caseUpdateValidation,
  isFilingEvent,
  isHearingEvent,
  isOrderEvent,
  matterFieldLabels,
  stageTransitionWarning,
  stagesForLaw,
} from '../../utils/matterUtils';

const today = () => new Date().toISOString().slice(0, 10);

const empty = (matter) => ({
  entryDate: today(),
  entryType: 'General Update',
  proceedingStage: matter?.currentStage || stagesForLaw(matter?.lawType, matter?.matterTemplate)[0],
  matterStatus: matter?.status || 'Open',
  title: '',
  description: '',
  currentStatus: '',
  nextAction: '',
  nextActionDate: '',
  updatePresentStatus: true,
  hearingDate: '',
  hearingNoticeDate: '',
  attended: '',
  attendedBy: '',
  hearingMode: matter?.lawType === 'Income Tax' ? 'Faceless' : 'Physical',
  hearingPurpose: '',
  hearingOutcome: '',
  adjournmentSought: false,
  adjournmentReason: '',
  nextHearingDate: '',
  documentsRequired: '',
  filingDate: '',
  acknowledgementNumber: '',
  documentsFiled: '',
  orderDate: '',
  orderNumber: '',
  orderSection: '',
  orderOutcome: '',
  demandConfirmed: '',
  demandDropped: '',
  interest: '',
  penalty: '',
  reliefGranted: '',
  orderSummary: '',
  appealRequired: '',
  appealDueDate: '',
  transitionToStage: '',
});

export default function CaseUpdateModal({ open, onClose, onSave, matter }) {
  const [values, setValues] = useState(empty(matter));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const stages = useMemo(() => { const configured=(matter?.templateSnapshot?.stages||[]).map(x=>typeof x==='string'?x:x.name).filter(Boolean); return configured.length?configured:stagesForLaw(matter?.lawType, matter?.matterTemplate); }, [matter?.lawType, matter?.matterTemplate, matter?.templateSnapshot]);
  const labels = matterFieldLabels(matter?.lawType);

  useEffect(() => {
    if (open) {
      setValues(empty(matter));
      setErrors({});
    }
  }, [open, matter]);

  if (!open) return null;

  const set = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '' }));
  };

  const eventChange = (type) => {
    const suggested = {
      'Hearing Attended': 'Hearing attended',
      'Hearing Not Attended': 'Hearing not attended',
      'Adjournment Requested': 'Adjournment sought',
      'Reply Filed': 'Reply filed',
      'Order Passed': 'Order passed',
      'Appeal Filed': 'Appeal filed',
      'Stage Transition': 'Proceeding moved to next stage',
    }[type] || '';

    setValues((current) => ({
      ...current,
      entryType: type,
      title: current.title || suggested,
      attended: type === 'Hearing Attended' ? 'Yes' : type === 'Hearing Not Attended' ? 'No' : current.attended,
      hearingDate: isHearingEvent(type) ? (current.hearingDate || current.entryDate) : current.hearingDate,
      filingDate: isFilingEvent(type) ? (current.filingDate || current.entryDate) : current.filingDate,
      orderDate: isOrderEvent(type) ? (current.orderDate || current.entryDate) : current.orderDate,
    }));
    setErrors({});
  };

  const submit = async (event) => {
    event.preventDefault();
    const validation = caseUpdateValidation(values);
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    if (values.entryType === 'Stage Transition') {
      const warning = matter?.templateId ? '' : stageTransitionWarning(matter?.currentStage, values.transitionToStage, matter?.lawType);
      if (warning && !window.confirm(warning)) return;
    }

    setSaving(true);
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      setErrors({ form: error.message || 'Could not save update.' });
    } finally {
      setSaving(false);
    }
  };

  const input = (field) => `mt-1 w-full border rounded px-3 py-2 ${errors[field] ? 'border-red-500' : ''}`;
  const ErrorText = ({ field }) => errors[field] ? <p className="text-red-600 text-xs mt-1">{errors[field]}</p> : null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-screen overflow-y-auto">
        <div className="flex justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold">Add Case Update</h2>
            <p className="text-sm text-gray-500">{matter?.lawType} · {matter?.currentStage}</p>
          </div>
          <button type="button" onClick={onClose} className="text-2xl">×</button>
        </div>

        {errors.form && <div className="mb-3 p-2 bg-red-100 text-red-700 rounded">{errors.form}</div>}

        <div className="grid md:grid-cols-3 gap-4">
          <label className="text-sm">Date *
            <input type="date" value={values.entryDate} onChange={(e) => set('entryDate', e.target.value)} className={input('entryDate')} />
            <ErrorText field="entryDate" />
          </label>
          <label className="text-sm">Update Type
            <select value={values.entryType} onChange={(e) => eventChange(e.target.value)} className={input('entryType')}>
              {CASE_EVENT_TYPES.map((item) => <option key={item}>{item}</option>)}
            </select>
          </label>
          <label className="text-sm">Proceeding Stage *
            <select value={values.proceedingStage} onChange={(e) => set('proceedingStage', e.target.value)} className={input('proceedingStage')}>
              {stages.map((item) => <option key={item}>{item}</option>)}
            </select>
            <ErrorText field="proceedingStage" />
          </label>
        </div>

        {values.entryType === 'Stage Transition' && (
          <div className="mt-4 p-4 bg-purple-50 rounded">
            <label className="text-sm font-medium">Move to Stage *
              <select value={values.transitionToStage} onChange={(e) => set('transitionToStage', e.target.value)} className={input('transitionToStage')}>
                <option value="">Select next stage</option>
                {stages.filter((item) => item !== matter?.currentStage).map((item) => <option key={item}>{item}</option>)}
              </select>
              <ErrorText field="transitionToStage" />
            </label>
          </div>
        )}

        <label className="block text-sm mt-4">Short Heading *
          <input value={values.title} onChange={(e) => set('title', e.target.value)} className={input('title')} />
          <ErrorText field="title" />
        </label>
        <label className="block text-sm mt-4">Detailed Remarks
          <textarea rows="4" value={values.description} onChange={(e) => set('description', e.target.value)} className={input('description')} />
          <ErrorText field="description" />
        </label>

        {isHearingEvent(values.entryType) && (
          <div className="mt-5 p-4 border rounded">
            <h3 className="font-semibold mb-3">Hearing Details</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <label className="text-sm">Hearing Date
                <input type="date" value={values.hearingDate} onChange={(e) => set('hearingDate', e.target.value)} className={input('hearingDate')} />
                <ErrorText field="hearingDate" />
              </label>
              <label className="text-sm">Hearing Notice Date
                <input type="date" value={values.hearingNoticeDate} onChange={(e) => set('hearingNoticeDate', e.target.value)} className={input('hearingNoticeDate')} />
              </label>
              <label className="text-sm">Mode
                <select value={values.hearingMode} onChange={(e) => set('hearingMode', e.target.value)} className={input('hearingMode')}>
                  {HEARING_MODES.map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label className="text-sm">Attended
                <select value={values.attended} onChange={(e) => set('attended', e.target.value)} className={input('attended')}>
                  <option value="">Not specified</option><option>Yes</option><option>No</option>
                </select>
                <ErrorText field="attended" />
              </label>
              <label className="text-sm">Attended By
                <input value={values.attendedBy} onChange={(e) => set('attendedBy', e.target.value)} className={input('attendedBy')} />
              </label>
              <label className="text-sm">Next Hearing Date
                <input type="date" value={values.nextHearingDate} onChange={(e) => set('nextHearingDate', e.target.value)} className={input('nextHearingDate')} />
                <ErrorText field="nextHearingDate" />
              </label>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <label className="text-sm">Purpose
                <input value={values.hearingPurpose} onChange={(e) => set('hearingPurpose', e.target.value)} className={input('hearingPurpose')} />
              </label>
              <label className="text-sm">Outcome
                <input value={values.hearingOutcome} onChange={(e) => set('hearingOutcome', e.target.value)} className={input('hearingOutcome')} />
                <ErrorText field="hearingOutcome" />
              </label>
              <label className="text-sm">Documents / Evidence Required
                <textarea rows="2" value={values.documentsRequired} onChange={(e) => set('documentsRequired', e.target.value)} className={input('documentsRequired')} />
              </label>
              <div>
                <label className="flex items-center gap-2 mt-6">
                  <input type="checkbox" checked={values.adjournmentSought} onChange={(e) => set('adjournmentSought', e.target.checked)} />
                  <span className="text-sm">Adjournment sought</span>
                </label>
                {(values.adjournmentSought || values.entryType === 'Adjournment Requested') && (
                  <>
                    <input value={values.adjournmentReason} onChange={(e) => set('adjournmentReason', e.target.value)} className={input('adjournmentReason')} placeholder="Reason for adjournment" />
                    <ErrorText field="adjournmentReason" />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {isFilingEvent(values.entryType) && (
          <div className="mt-5 p-4 border rounded">
            <h3 className="font-semibold mb-3">Filing Details</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <label className="text-sm">Filing Date *
                <input type="date" value={values.filingDate} onChange={(e) => set('filingDate', e.target.value)} className={input('filingDate')} />
                <ErrorText field="filingDate" />
              </label>
              <label className="text-sm">Acknowledgement / Reference Number
                <input value={values.acknowledgementNumber} onChange={(e) => set('acknowledgementNumber', e.target.value)} className={input('acknowledgementNumber')} />
                <ErrorText field="acknowledgementNumber" />
              </label>
              <label className="text-sm">Documents Filed
                <input value={values.documentsFiled} onChange={(e) => set('documentsFiled', e.target.value)} className={input('documentsFiled')} />
              </label>
            </div>
          </div>
        )}

        {isOrderEvent(values.entryType) && (
          <div className="mt-5 p-4 border rounded">
            <h3 className="font-semibold mb-3">Order Details</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <label className="text-sm">Order Date *
                <input type="date" value={values.orderDate} onChange={(e) => set('orderDate', e.target.value)} className={input('orderDate')} />
                <ErrorText field="orderDate" />
              </label>
              <label className="text-sm">Order Number
                <input value={values.orderNumber} onChange={(e) => set('orderNumber', e.target.value)} className={input('orderNumber')} />
              </label>
              <label className="text-sm">{labels.section}
                <input value={values.orderSection} onChange={(e) => set('orderSection', e.target.value)} className={input('orderSection')} />
              </label>
              <label className="text-sm">Outcome *
                <select value={values.orderOutcome} onChange={(e) => set('orderOutcome', e.target.value)} className={input('orderOutcome')}>
                  <option value="">Select outcome</option>{ORDER_OUTCOMES.map((item) => <option key={item}>{item}</option>)}
                </select>
                <ErrorText field="orderOutcome" />
              </label>
              <label className="text-sm">Appeal Required *
                <select value={values.appealRequired} onChange={(e) => set('appealRequired', e.target.value)} className={input('appealRequired')}>
                  <option value="">Select</option><option>Under Review</option><option>Yes</option><option>No</option>
                </select>
                <ErrorText field="appealRequired" />
              </label>
              <label className="text-sm">Appeal Due Date
                <input type="date" value={values.appealDueDate} onChange={(e) => set('appealDueDate', e.target.value)} className={input('appealDueDate')} />
                <ErrorText field="appealDueDate" />
              </label>
              <label className="text-sm">Demand Confirmed<input value={values.demandConfirmed} onChange={(e) => set('demandConfirmed', e.target.value)} className={input('demandConfirmed')} /></label>
              <label className="text-sm">Demand Dropped<input value={values.demandDropped} onChange={(e) => set('demandDropped', e.target.value)} className={input('demandDropped')} /></label>
              <label className="text-sm">Relief Granted<input value={values.reliefGranted} onChange={(e) => set('reliefGranted', e.target.value)} className={input('reliefGranted')} /></label>
              <label className="text-sm">Interest<input value={values.interest} onChange={(e) => set('interest', e.target.value)} className={input('interest')} /></label>
              <label className="text-sm">Penalty<input value={values.penalty} onChange={(e) => set('penalty', e.target.value)} className={input('penalty')} /></label>
            </div>
            <label className="block text-sm mt-4">Order Summary
              <textarea rows="3" value={values.orderSummary} onChange={(e) => set('orderSummary', e.target.value)} className={input('orderSummary')} />
            </label>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <label className="text-sm">Next Action
            <input value={values.nextAction} onChange={(e) => set('nextAction', e.target.value)} className={input('nextAction')} />
            <ErrorText field="nextAction" />
          </label>
          <label className="text-sm">Next Action Date
            <input type="date" value={values.nextActionDate} onChange={(e) => set('nextActionDate', e.target.value)} className={input('nextActionDate')} />
            <ErrorText field="nextActionDate" />
          </label>
        </div>
        <label className="block text-sm mt-4">Present Status after this update
          <input value={values.currentStatus} onChange={(e) => set('currentStatus', e.target.value)} className={input('currentStatus')} />
        </label>
        <label className="flex items-center gap-2 mt-4">
          <input type="checkbox" checked={values.updatePresentStatus} onChange={(e) => set('updatePresentStatus', e.target.checked)} />
          <span className="text-sm">Update the Matter's present status and next action</span>
        </label>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
          <button disabled={saving} className="px-4 py-2 bg-purple-700 text-white rounded">{saving ? 'Saving...' : 'Save Update'}</button>
        </div>
      </form>
    </div>
  );
}
